import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card } from "@/components/Card";
import { SectionHeader } from "@/components/SectionHeader";

// Das Handbuch wird als statische Markdown-Datei unter /app/handbuch.md
// ausgeliefert (liegt in frontend/public/handbuch.md, Bilder unter
// frontend/public/bilder/). Kein API-Call nötig.
const HANDBUCH_URL = "/app/handbuch.md";

// Slug-Erzeugung kompatibel mit GitHub/remark-gfm: kleinschreiben, Sonderzeichen
// entfernen, Leerzeichen zu Bindestrichen. Damit stimmen die Überschriften-IDs
// mit den TOC-Ankerlinks (#1-grundbegriffe--rollen) aus handbuch.md überein.
function slugify(node: React.ReactNode): string {
  const text = childrenToText(node);
  return text
    .trim()
    .toLowerCase()
    // GitHub-Slugs behalten Unicode-Buchstaben (ä, ü, …) und Ziffern; entfernt
    // werden nur Satz-/Sonderzeichen außer Bindestrich. Leerzeichen → "-".
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .replace(/\s/g, "-");
}

function childrenToText(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(childrenToText).join("");
  if (typeof node === "object" && "props" in (node as any)) {
    return childrenToText((node as any).props?.children);
  }
  return "";
}

export function HandbuchSection() {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let aborted = false;
    fetch(HANDBUCH_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!aborted) setMarkdown(text);
      })
      .catch(() => {
        if (!aborted) setError("Handbuch konnte nicht geladen werden.");
      });
    return () => {
      aborted = true;
    };
  }, []);

  return (
    <Card>
      <SectionHeader
        title="Handbuch"
        desc="Anwenderhandbuch für Buchungsstelle, Rechnungsstelle und Admin."
      />

      {error && (
        <div style={{ fontSize: 13, color: "var(--status-cancel)", padding: "8px 0" }}>{error}</div>
      )}
      {!markdown && !error && (
        <div style={{ fontSize: 13, color: "var(--fg-muted)", padding: "8px 0" }}>Lade…</div>
      )}

      {markdown && (
        <div className="handbuch-md" style={{ fontSize: 14, lineHeight: 1.6, color: "var(--fg)" }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ node: _n, ...props }) => (
                <h1
                  id={slugify(props.children)}
                  className="display"
                  style={{ fontSize: 26, color: "var(--brand-900)", fontWeight: 400, margin: "8px 0 16px", scrollMarginTop: 16 }}
                  {...props}
                />
              ),
              h2: ({ node: _n, ...props }) => (
                <h2
                  id={slugify(props.children)}
                  style={{
                    fontSize: 19,
                    color: "var(--brand-900)",
                    fontWeight: 600,
                    margin: "28px 0 10px",
                    paddingBottom: 6,
                    borderBottom: "1px solid var(--border)",
                    scrollMarginTop: 16,
                  }}
                  {...props}
                />
              ),
              h3: ({ node: _n, ...props }) => (
                <h3
                  id={slugify(props.children)}
                  style={{ fontSize: 15, color: "var(--fg)", fontWeight: 700, margin: "20px 0 8px", scrollMarginTop: 16 }}
                  {...props}
                />
              ),
              p: (props) => <p style={{ margin: "8px 0" }} {...props} />,
              ul: (props) => <ul style={{ margin: "8px 0", paddingLeft: 22 }} {...props} />,
              ol: (props) => <ol style={{ margin: "8px 0", paddingLeft: 22 }} {...props} />,
              li: (props) => <li style={{ margin: "3px 0" }} {...props} />,
              a: ({ node: _n, href, ...props }) => {
                const isAnchor = typeof href === "string" && href.startsWith("#");
                return (
                  <a
                    href={href}
                    style={{ color: "var(--brand-700)", textDecoration: "none" }}
                    onClick={
                      isAnchor
                        ? (e) => {
                            // Interner Sprungmarken-Link: selbst scrollen, statt den
                            // Browser den Hash gegen die App-Route auflösen zu lassen
                            // (das würde sonst zur Dashboard-Route führen).
                            e.preventDefault();
                            const id = decodeURIComponent(href!.slice(1));
                            document
                              .getElementById(id)
                              ?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }
                        : undefined
                    }
                    {...props}
                  />
                );
              },
              hr: () => <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "24px 0" }} />,
              blockquote: (props) => (
                <blockquote
                  style={{
                    margin: "12px 0",
                    padding: "10px 16px",
                    background: "var(--bg-subtle)",
                    borderLeft: "3px solid var(--brand-700)",
                    borderRadius: "0 8px 8px 0",
                    color: "var(--fg-muted)",
                    fontSize: 13,
                  }}
                  {...props}
                />
              ),
              code: (props) => (
                <code
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "0.88em",
                    background: "var(--bg-subtle)",
                    padding: "1px 5px",
                    borderRadius: 4,
                  }}
                  {...props}
                />
              ),
              pre: (props) => (
                <pre
                  style={{
                    background: "var(--bg-subtle)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "12px 14px",
                    overflowX: "auto",
                    fontSize: 12.5,
                    lineHeight: 1.45,
                  }}
                  {...props}
                />
              ),
              table: (props) => (
                <div style={{ overflowX: "auto", margin: "12px 0" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }} {...props} />
                </div>
              ),
              th: (props) => (
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    borderBottom: "2px solid var(--border)",
                    background: "var(--bg-subtle)",
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                  {...props}
                />
              ),
              td: (props) => (
                <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", verticalAlign: "top" }} {...props} />
              ),
              img: (props) => (
                <img
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: 8,
                    border: "1px solid var(--border)",
                    margin: "8px 0",
                    display: "block",
                  }}
                  {...props}
                />
              ),
            }}
          >
            {markdown}
          </ReactMarkdown>
        </div>
      )}
    </Card>
  );
}
